import { useCallback, useEffect, useRef, useState } from "react";
import { sendAgentInvitation } from "@/api/directoryApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Modal for inviting a human support agent by email and role.
 * Calls the authenticated BFF endpoint; no credentials ever appear in the UI.
 *
 * @param {{ open: boolean, onClose: () => void, workspaceName?: string, roleOptions?: { value: string, label: string }[] }} props
 */
export default function InviteAgentModal({ open, onClose, workspaceName, roleOptions = [] }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState(/** @type {"idle"|"loading"|"success"|"error"} */ ("idle"));
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setEmail("");
      setRole(roleOptions.find((opt) => opt.value === "chat")?.value || roleOptions[0]?.value || "");
      setStatus("idle");
      setErrorMsg("");
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open, roleOptions]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = email.trim().toLowerCase();
      if (!EMAIL_REGEX.test(trimmed)) {
        setErrorMsg("Please enter a valid email address.");
        return;
      }
      if (!role) {
        setErrorMsg("No workspace roles are available. Reload and try again.");
        return;
      }
      setStatus("loading");
      setErrorMsg("");
      try {
        await sendAgentInvitation({ email: trimmed, role });
        setStatus("success");
      } catch (err) {
        setStatus("error");
        const code = err?.data?.detail?.error;
        if (code === "invitation_already_pending") {
          setErrorMsg("An invitation already exists. Use Reinvite after it expires, or Revoke it first.");
        } else if (code === "agent_already_active") {
          setErrorMsg("This agent already has access. Change their role instead of inviting again.");
        } else {
          setErrorMsg(err instanceof Error ? err.message : "Failed to send invitation.");
        }
      }
    },
    [email, role]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/60 mx-4">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <h2
              id="invite-modal-title"
              className="text-base font-semibold text-slate-900"
            >
              Invite support agent
            </h2>
            {workspaceName ? (
              <p className="mt-0.5 text-xs text-slate-500">
                Workspace: <span className="font-medium text-slate-700">{workspaceName}</span>
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-4 mt-0.5 text-slate-400 transition hover:text-slate-600"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {status === "success" ? (
            <div className="flex flex-col items-center py-6 text-center">
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <i className="ri-mail-send-line text-2xl" />
              </span>
              <p className="text-sm font-semibold text-slate-900">Invitation sent!</p>
              <p className="mt-1 text-xs text-slate-500">
                An invitation email has been sent. The link is valid for{" "}
                <span className="font-medium text-slate-700">24 hours</span> and can only be
                used once.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-teal-700"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <p className="mb-4 text-sm text-slate-600">
                The invited agent will receive a secure link to create their account.
                <strong className="font-medium text-slate-700"> No credentials are included in the email.</strong>
              </p>

              {/* Email */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="invite-email" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Agent email address
                  </label>
                  <input
                    ref={inputRef}
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errorMsg) setErrorMsg("");
                    }}
                    placeholder="agent@example.com"
                    className="ui-input w-full"
                    disabled={status === "loading"}
                    autoComplete="off"
                    required
                  />
                </div>

                {/* Role */}
                <div>
                  <label htmlFor="invite-role" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Workspace role
                  </label>
                  <select
                    id="invite-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={status === "loading" || roleOptions.length === 0}
                    className="ui-input w-full"
                  >
                    {roleOptions.length === 0 ? <option value="">No roles available</option> : null}
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Roles and page access are configured in workspace RBAC settings.
                    Home dashboard access is included automatically.
                  </p>
                </div>
              </div>

              {status === "error" && errorMsg ? (
                <p className="mt-3 text-xs text-rose-600">{errorMsg}</p>
              ) : errorMsg ? (
                <p className="mt-3 text-xs text-rose-600">{errorMsg}</p>
              ) : null}

              <div className="mt-5 flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={status === "loading"}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={status === "loading" || !email.trim() || !role}
                  className="flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line text-sm" />
                      Send invitation
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
